import { useMemo, useState } from 'react';
import type { FormEvent } from 'react';

import {
  Alert,
  Button,
  Card,
  Field,
  Heading,
  Inline,
  Input,
  Select,
  Spinner,
  Stack,
  Text,
} from '../design/index.js';
import { createGoal, deleteGoal, updateGoal } from '../api.js';
import type { GoalOutput } from '../api.js';
import styles from './GoalForm.module.css';

/**
 * One node a goal could target, as GoalForm needs it.
 *
 * Deliberately its own shape rather than the `NodeSummary` already in
 * `api.ts`: that type has no notion of "source" (which provider feeds this
 * node, or none), and `api.ts` is off limits for changing existing types —
 * a sibling wave's code depends on them. The orchestrator maps whatever it
 * fetches (nodes, or orbit items) into this before mounting the form.
 */
export interface GoalFormNode {
  id: string;
  title: string;
  unit: string;
  precision: number;
  latestValue: number | null;
  source: 'manual' | 'withings' | 'import';
}

export interface GoalFormProps {
  /** Every node a goal could target. Empty means nothing to connect yet. */
  nodes: GoalFormNode[];
  /** Omit (or pass null/undefined) to create a new goal. Pass an existing
   *  goal to prefill the form, PATCH on submit, and offer deleting it. */
  goal?: GoalOutput | null;
  /** Fired after a successful create or update, with the saved goal. */
  onSaved: (goal: GoalOutput) => void;
  /** Fired after a successful delete, with the deleted goal's id. */
  onDeleted?: (goalId: string) => void;
  /** Renders a Cancel control when provided. Omit to hide it. */
  onCancel?: () => void;
  className?: string;
}

function sourceLabel(source: GoalFormNode['source']): string {
  switch (source) {
    case 'withings':
      return 'Withings';
    case 'import':
      return 'Imported';
    case 'manual':
    default:
      return 'Entered by hand';
  }
}

/** "kg — you are at 113.3 now": a target typed without knowing the starting
 *  point is a guess, so the field that asks for one always shows it. */
function currentValueHint(node: GoalFormNode | undefined): string | undefined {
  if (!node) return undefined;
  if (node.latestValue === null) return `${node.unit} — no readings yet`;
  return `${node.unit} — you are at ${node.latestValue.toFixed(node.precision)} now`;
}

type Phase =
  | { kind: 'editing' }
  | { kind: 'submitting' }
  | { kind: 'delete-confirm' }
  | { kind: 'deleting' };

/**
 * Add-a-goal / edit-a-goal, in one form.
 *
 * Field order is: title, then which measurement, then the target (shown
 * against the unit and the current value), then an optional deadline —
 * see the brief report for why. Direction is never asked: the node
 * catalog already knows it, and the server derives the goal's direction
 * from that, so the form cannot contradict it.
 */
export function GoalForm({ nodes, goal, onSaved, onDeleted, onCancel, className }: GoalFormProps) {
  const isEditing = goal != null;

  const [title, setTitle] = useState(goal?.title ?? '');
  const [nodeId, setNodeId] = useState(goal?.nodeId ?? nodes[0]?.id ?? '');
  const [targetValue, setTargetValue] = useState(goal ? String(goal.targetValue) : '');
  const [targetDate, setTargetDate] = useState(goal?.targetDate ? goal.targetDate.slice(0, 10) : '');
  /**
   * Blank means "now", which is what every goal did before this field
   * existed. Only offered when CREATING: the baseline was captured against
   * the start date, so moving it afterwards would leave a goal measuring
   * from a weight it never recorded.
   */
  const [startDate, setStartDate] = useState('');

  const [phase, setPhase] = useState<Phase>({ kind: 'editing' });
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Defensive: if editing a goal whose node isn't in `nodes` (it
  // should always be, but a stale list is cheap to guard against), keep the
  // select from silently defaulting to the wrong option.
  const selectOptions = useMemo(() => {
    if (!nodeId || nodes.some((n) => n.id === nodeId)) return nodes;
    return [
      { id: nodeId, title: 'This measurement', unit: '', precision: 0, latestValue: null, source: 'manual' as const },
      ...nodes,
    ];
  }, [nodes, nodeId]);

  const selectedNode = selectOptions.find((n) => n.id === nodeId);
  const busy = phase.kind === 'submitting' || phase.kind === 'deleting';

  if (nodes.length === 0 && !isEditing) {
    return (
      <Card className={className} data-testid="goal-form-no-nodes">
        <Stack gap={3}>
          <Heading level={3} size="lg">Nothing to set a goal on yet</Heading>
          <Text size="sm" color="secondary">
            Connect Withings, or add something to track by hand, first — a goal always
            aims at a measurement that already exists.
          </Text>
        </Stack>
      </Card>
    );
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;

    const trimmedTitle = title.trim();
    const parsedTarget = Number(targetValue);
    if (!trimmedTitle || !nodeId || targetValue.trim() === '' || Number.isNaN(parsedTarget)) {
      setSubmitError('Fill in a title, a measurement and a target value.');
      return;
    }

    setPhase({ kind: 'submitting' });
    setSubmitError(null);
    // A deadline is a whole day, not the instant it starts. `new Date('2026-09-16')`
    // is UTC midnight, so picking today would be "in the past" from mid-morning
    // onward and the server would refuse it. End of day makes the deadline day
    // itself count, which is what "by the 16th" means to a person.
    const isoTargetDate = targetDate ? new Date(`${targetDate}T23:59:59.999Z`).toISOString() : null;
    // Start of the chosen day, where the deadline takes the end of its own —
    // a goal set on the 5th ran for all of the 5th.
    const isoStartDate = startDate ? new Date(`${startDate}T00:00:00.000Z`).toISOString() : undefined;
    try {
      const saved =
        isEditing && goal
          ? await updateGoal(goal.id, { title: trimmedTitle, targetValue: parsedTarget, targetDate: isoTargetDate })
          : await createGoal({
              nodeId,
              title: trimmedTitle,
              targetValue: parsedTarget,
              targetDate: isoTargetDate,
              ...(isoStartDate ? { startDate: isoStartDate } : {}),
            });
      setPhase({ kind: 'editing' });
      onSaved(saved);
    } catch (err) {
      setPhase({ kind: 'editing' });
      setSubmitError(err instanceof Error ? err.message : 'Could not save this goal.');
    }
  }

  async function handleDelete() {
    if (!goal) return;
    setPhase({ kind: 'deleting' });
    setDeleteError(null);
    try {
      await deleteGoal(goal.id);
      onDeleted?.(goal.id);
    } catch (err) {
      setPhase({ kind: 'delete-confirm' });
      setDeleteError(err instanceof Error ? err.message : 'Could not delete this goal.');
    }
  }

  return (
    <Card className={className} data-testid={isEditing ? 'goal-form-edit' : 'goal-form-create'}>
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap={5}>
          <Heading level={3} size="lg">{isEditing ? 'Edit goal' : 'Add a goal'}</Heading>

          <Field label="Title" required>
            <Input
              data-testid="goal-form-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              placeholder="e.g. Back to 75 kg"
              disabled={busy}
              required
            />
          </Field>

          <Field
            label="Measurement"
            required
            hint={
              isEditing
                ? "A goal's measurement can't change after it's set — delete this one and add a new goal to aim at something else."
                : undefined
            }
          >
            <Select
              data-testid="goal-form-node"
              value={nodeId}
              onChange={(event) => setNodeId(event.target.value)}
              disabled={busy || isEditing}
              required
            >
              {selectOptions.map((node) => (
                <option key={node.id} value={node.id}>
                  {node.title} — {sourceLabel(node.source)}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Target" required hint={currentValueHint(selectedNode)}>
            <Input
              data-testid="goal-form-target"
              type="number"
              inputMode="decimal"
              step={selectedNode ? 10 ** -selectedNode.precision : 'any'}
              value={targetValue}
              onChange={(event) => setTargetValue(event.target.value)}
              disabled={busy}
              required
            />
          </Field>

          {/* Creating only. Editing it later would strand the baseline, which
              was captured against whatever start date this goal was given. */}
          {!goal && (
            <Field
              label="Started on (optional)"
              hint="Blank starts it today. Backdate it and progress is measured from your weight on that date, not from today's."
            >
              <Input
                data-testid="goal-form-start-date"
                type="date"
                value={startDate}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(event) => setStartDate(event.target.value)}
                disabled={busy}
              />
            </Field>
          )}

          <Field
            label="Deadline (optional)"
            hint="Leave this blank for an open-ended goal — one with progress, but no due date."
          >
            <Input
              data-testid="goal-form-date"
              type="date"
              value={targetDate}
              onChange={(event) => setTargetDate(event.target.value)}
              disabled={busy}
            />
          </Field>

          {submitError && (
            <Alert variant="error" title="Couldn't save this goal" data-testid="goal-form-error">
              {submitError}
            </Alert>
          )}

          <Inline gap={3} align="center" wrap>
            <Button type="submit" data-testid="goal-form-submit" disabled={busy}>
              {phase.kind === 'submitting' ? 'Saving…' : isEditing ? 'Save changes' : 'Add goal'}
            </Button>
            {phase.kind === 'submitting' && (
              <Spinner size="sm" aria-label="Saving this goal" data-testid="goal-form-saving" />
            )}
            {onCancel && (
              <Button
                type="button"
                variant="ghost"
                data-testid="goal-form-cancel"
                onClick={onCancel}
                disabled={busy}
              >
                Cancel
              </Button>
            )}
          </Inline>

          {isEditing && goal && (
            <div className={styles.deleteZone}>
              {phase.kind !== 'delete-confirm' && phase.kind !== 'deleting' ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  data-testid="goal-form-delete-button"
                  onClick={() => setPhase({ kind: 'delete-confirm' })}
                >
                  Delete goal
                </Button>
              ) : (
                <Inline gap={3} align="center" wrap data-testid="goal-form-delete-confirm">
                  <Text size="sm" color="secondary">
                    Delete this goal? Every measurement you&rsquo;ve recorded stays exactly
                    where it is — only the target goes away.
                  </Text>
                  <Button
                    type="button"
                    variant="danger"
                    size="sm"
                    data-testid="goal-form-delete-confirm-button"
                    onClick={handleDelete}
                    disabled={phase.kind === 'deleting'}
                  >
                    {phase.kind === 'deleting' ? 'Deleting…' : 'Yes, delete'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    disabled={phase.kind === 'deleting'}
                    onClick={() => setPhase({ kind: 'editing' })}
                  >
                    Cancel
                  </Button>
                </Inline>
              )}
              {deleteError && (
                <Alert variant="error" title="Couldn't delete this goal" data-testid="goal-form-delete-error">
                  {deleteError}
                </Alert>
              )}
            </div>
          )}
        </Stack>
      </form>
    </Card>
  );
}
